import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../../lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogTitle,
} from "../../ui/alert-dialog";
import { CrmSummary } from "./CrmSummary";
import { CrmToolbar } from "./CrmToolbar";
import { ClientList } from "./ClientList";
import { ClientDialog } from "./ClientDialog";

const fail = (e) => toast.error(formatApiError(e.response?.data?.detail));
const PAYLOAD_KEYS = ["admin_id", "client_name", "date", "time", "registered", "deposited", "purchased", "notes"];

export const CrmPanel = ({ me }) => {
  const [admins, setAdmins] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", admin: "all", status: "all", sort: "desc" });
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const isOwner = me?.role === "owner";
  const canEdit = useCallback((c) => isOwner || c.admin_id === me?.id, [isOwner, me]);

  const params = useMemo(() => {
    const p = { sort: filters.sort };
    if (filters.search.trim()) p.search = filters.search.trim();
    if (filters.admin !== "all") p.admin_id = filters.admin;
    if (filters.status !== "all") {
      const [k, v] = filters.status.split(":");
      p[k] = v;
    }
    return p;
  }, [filters]);

  const refresh = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get("/admin/clients", { params }), api.get("/admin/clients/summary")]);
      setClients(Array.isArray(c.data) ? c.data : []);
      setSummary(s.data ?? null);
    } catch (e) {
      // keep last data on transient errors
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    api.get("/admin/admins").then((r) => setAdmins(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(refresh, 250);
    const iv = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, 15000);
    return () => { clearTimeout(t); clearInterval(iv); };
  }, [refresh]);

  const onToggle = async (c, key, value) => {
    setClients((list) => list.map((x) => (x.id === c.id ? { ...x, [key]: value } : x)));
    try {
      await api.patch(`/admin/clients/${c.id}`, { [key]: value });
    } catch (e) {
      fail(e);
    }
    refresh();
  };

  const onSave = async (form) => {
    const body = Object.fromEntries(PAYLOAD_KEYS.map((k) => [k, form[k]]));
    try {
      if (form.id) await api.patch(`/admin/clients/${form.id}`, body);
      else await api.post("/admin/clients", body);
      toast.success(form.id ? "Client updated" : "Client added");
      refresh();
      return true;
    } catch (e) {
      fail(e);
      return false;
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/clients/${toDelete.id}`);
      toast.success("Client deleted");
      refresh();
    } catch (e) {
      fail(e);
    }
    setToDelete(null);
  };

  return (
    <div className="space-y-6" data-testid="crm-panel">
      <CrmSummary summary={summary} meId={me?.id} />
      <CrmToolbar filters={filters} setFilters={setFilters} admins={admins} onAdd={() => setEditing({})} />
      <ClientList clients={clients} loading={loading} onToggle={onToggle} onEdit={setEditing} onDelete={setToDelete} canEdit={canEdit} />
      <ClientDialog client={editing} admins={admins} meId={me?.id} isOwner={isOwner} onClose={() => setEditing(null)} onSave={onSave} />
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="crm-delete-dialog" className="rounded-[28px] border-white/10 bg-surface text-zinc-100">
          <AlertDialogTitle className="font-display">Delete {toDelete?.client_name}?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">This removes the client for all admins and cannot be undone.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="crm-delete-cancel" className="rounded-full border-white/10 bg-transparent text-zinc-200 hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="crm-delete-confirm" onClick={confirmDelete} className="rounded-full bg-red-500 text-white hover:bg-red-500/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
